/*
 * UserMenu — Overlay-Menü bei Klick auf Username in Tab-Leiste
 *
 * Zeigt Profil-Infos (Name, Email, optional Avatar), Logout, Account-Loeschung
 * sowie Editor-Eintraege fuer AI-Memory und Personal Dictionary.
 *
 * Rendering folgt der neuen Menu-API (drawMenuFrame + drawMenuItem), Items
 * tragen [K]-Hotkeys. Avatar wird im Prop-Mode als DOM-img ueber dem Canvas
 * eingeblendet — Canvas hat keine native Bitmap-Pipeline, und ein einzelner
 * HTML-img ist deutlich leichter als eine eigene Render-Phase fuer ein
 * 36×36px-Profilbild.
 */

import { Style } from './config.js'
import { blurKeyboardSink } from './touchKeyboard.js'

export class UserMenu {
    /**
     * @param {import('./overlay.js').Overlay} overlay
     * @param {import('./DialogManager.js').DialogManager} dialogManager
     * @param {import('./auth/AuthService.js').AuthService} authService
     * @param {object} [opts]
     * @param {Function} [opts.onEditMemory] - async () => void; wenn gesetzt,
     *   erscheint "Edit Memory" als erster Menue-Eintrag (Spec §6 Phase 5)
     * @param {Function} [opts.onEditPersonalDict] - async () => void; wenn
     *   gesetzt, erscheint "Edit Personal Dictionary" (Spellcheck §10 Phase 4)
     * @param {import('./Renderer.js').Renderer} [opts.renderer] - Fuer
     *   Mode-Lookup (Avatar nur Prop-Mode) und Pixel-Position des Avatar-img.
     *   Optional — ohne Renderer wird der Avatar nicht angezeigt.
     */
    constructor(overlay, dialogManager, authService, opts = {}) {
        this._overlay = overlay
        this._dialogManager = dialogManager
        this._authService = authService
        this._onEditMemory = typeof opts.onEditMemory === 'function'
            ? opts.onEditMemory : null
        this._onEditPersonalDict = typeof opts.onEditPersonalDict === 'function'
            ? opts.onEditPersonalDict : null
        this._renderer = opts.renderer || null

        this.isOpen = false
        this._selectedIndex = 0
        this._items = []

        // Layout-Cache
        this._menuX = 0
        this._menuY = 0
        this._menuWidth = 0
        this._menuHeight = 0
        this._itemStartY = 0   // Y-Zeile des ersten Action-Items

        // Avatar-DOM-Element (lazy erstellt)
        this._avatarImg = null
    }

    /**
     * Oeffnet das User-Menu.
     * @param {Object} user - { id, email, name, avatarUrl, role }
     * @param {number} cols - Bildschirmbreite in Zeichen
     */
    show(user, cols) {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        this.isOpen = true
        this._selectedIndex = 0
        // Fuer Re-Render aus handleMouseMove (Hover-Selection)
        this._user = user
        this._cols = cols

        // Action-Items zusammenstellen. Hotkeys M/P/A/F/L/D — kollisionsfrei.
        this._items = []
        if (this._onEditMemory) {
            this._items.push({ key: 'M', label: 'Edit Memory', action: 'editMemory' })
        }
        if (this._onEditPersonalDict) {
            this._items.push({ key: 'P', label: 'Edit Personal Dictionary', action: 'editPersonalDict' })
        }
        if (user.role === 'admin') {
            this._items.push({ key: 'A', label: 'Admin Panel', action: 'admin' })
        }
        this._items.push({ key: 'F', label: 'Send Feedback', action: 'feedback' })
        this._items.push({ key: 'L', label: 'Logout', action: 'logout' })
        this._items.push({ key: 'D', label: 'Delete account', action: 'delete' })

        this._render(user, cols)
    }

    /**
     * Schliesst das Menu.
     */
    close() {
        this.isOpen = false
        this._hideAvatar()
        this._overlay.deactivate()
    }

    /**
     * Rendert das Menu im Overlay.
     */
    _render(user, cols) {
        this._overlay.clear()

        const name = user.name || user.email || 'User'
        const email = user.email || ''

        // Avatar nur im Prop-Mode — im Mono-Mode passt ein Bitmap nicht zum
        // Terminal-Look und Char-Aspect-Ratio macht das Layout unsauber.
        const propMode = !!(this._renderer && this._renderer.isProportional && this._renderer.isProportional())
        const showAvatar = propMode && !!user.avatarUrl
        const cw = this._renderer ? this._renderer.charWidth : 0
        const ch = this._renderer ? this._renderer.charHeight : 0

        // Avatar ist quadratisch und 2 Zeilen (Name + Email) hoch — also
        // 2*ch Pixel breit. Anzahl reservierter Char-Spalten = ceil(2*ch / cw)
        // plus 1 Spalte Padding zum Text.
        const avatarChars = showAvatar && cw > 0 && ch > 0
            ? Math.ceil((2 * ch) / cw) + 1
            : 0

        // Breite: Header braucht Avatar-Block + laengste Header-Zeile,
        // Action-Items brauchen "[K] Label"-Breite. Innen-Padding = 1.
        const headerTextLen = Math.max(name.length, email.length)
        const itemTextLen = this._items.length > 0
            ? Math.max(...this._items.map(i => i.label.length + 4)) // "[K] "
            : 0
        const innerWidth = Math.max(avatarChars + headerTextLen, itemTextLen)
        const width = Math.min(innerWidth + 4, 50)   // 4 = 2x Inset-Padding

        const headerRows = 2          // Name-Zeile + Email-Zeile
        const dividerRows = 1
        const height = 1 + headerRows + dividerRows + this._items.length + 1

        // Position: rechts oben (unter der Tab-Leiste)
        const x = Math.max(0, cols - width - 2)
        const y = 1

        this._menuX = x
        this._menuY = y
        this._menuWidth = width
        this._menuHeight = height
        this._itemStartY = y + 1 + headerRows + dividerRows

        // Frame
        this._overlay.drawMenuFrame(
            { x, y, width, height },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        // Header-Bereich: Name + Email als proportionale Spans, rechts vom
        // Avatar (falls vorhanden). Im Mono-Mode setzt addTextSpan auf den
        // Mono-Fallback zurueck.
        const headerX = x + 1 + avatarChars
        const headerMaxWidth = Math.max(0, width - 2 - avatarChars)
        this._overlay.addTextSpan(headerX, y + 1, name, Style.PANE_TITLE, headerMaxWidth)
        if (email) {
            this._overlay.addTextSpan(headerX, y + 2, email, Style.PANE_INFO, headerMaxWidth)
        }

        // Divider — volle Linie zwischen Header und Action-Items
        const dividerY = y + 1 + headerRows
        for (let i = 1; i < width - 1; i++) {
            this._overlay.setChar(x + i, dividerY, '─', Style.MENU_BORDER)
        }

        // Action-Items via drawMenuItem (gibt [K]-Brackets + Selection-Stripe
        // gratis dazu)
        const itemX = x + 1
        const itemWidth = width - 2
        this._items.forEach((item, idx) => {
            this._overlay.drawMenuItem(
                itemX, this._itemStartY + idx,
                {
                    key: item.key,
                    label: item.label,
                    hasSubmenu: false,
                    isCurrent: false,
                    isDisabled: false
                },
                { selected: idx === this._selectedIndex, itemWidth }
            )
        })

        // Avatar-img positionieren (oder verstecken). Quadratisch 2*ch × 2*ch,
        // linksbuendig in der reservierten Spalte.
        if (showAvatar) {
            this._showAvatar(user.avatarUrl, x + 1, y + 1, ch)
        } else {
            this._hideAvatar()
        }

        this._overlay.isDirty = true
        this._overlay.isActive = true
    }

    /**
     * Positioniert und zeigt das Avatar-img. CSS-Pixel-Koordinaten werden aus
     * den Char-Koordinaten + dem Grid-Offset (zentrale Canvas-Einrueckung)
     * berechnet.
     */
    _showAvatar(url, charX, charY, ch) {
        const cw = this._renderer.charWidth
        const cols = Math.floor(window.innerWidth / cw)
        const rows = Math.floor(window.innerHeight / ch)
        const offsetX = (window.innerWidth - cols * cw) / 2
        const offsetY = (window.innerHeight - rows * ch) / 2
        // +4px Versatz nach rechts: optische Distanz zur Frame-Border
        const left = offsetX + charX * cw + 4
        const top = offsetY + charY * ch
        const size = 2 * ch  // quadratisch ueber 2 Zeilen

        if (!this._avatarImg) {
            this._avatarImg = document.createElement('img')
            this._avatarImg.alt = ''
            // Google-Avatar-URLs verlangen no-referrer (sonst 403)
            this._avatarImg.referrerPolicy = 'no-referrer'
            this._avatarImg.style.cssText = `
                position: fixed;
                object-fit: cover;
                pointer-events: none;
                z-index: 100;
                border-radius: 50%;
                display: none;
            `
            // Bei 403/404 still ausblenden — kein invalides Bild zeigen
            this._avatarImg.onerror = () => {
                if (this._avatarImg) this._avatarImg.style.display = 'none'
            }
            document.body.appendChild(this._avatarImg)
        }

        if (this._avatarImg.src !== url) {
            this._avatarImg.src = url
        }
        this._avatarImg.style.left = left + 'px'
        this._avatarImg.style.top = top + 'px'
        this._avatarImg.style.width = size + 'px'
        this._avatarImg.style.height = size + 'px'
        this._avatarImg.style.display = 'block'
    }

    /**
     * Versteckt das Avatar-img (DOM-Element bleibt fuer Wiederverwendung).
     */
    _hideAvatar() {
        if (this._avatarImg) {
            this._avatarImg.style.display = 'none'
        }
    }

    /**
     * Keyboard-Handler. Pfeil-Hoch/Runter + Enter wie gehabt, plus [K]-
     * Hotkeys: jeder Action-Item kann per Buchstabe direkt aktiviert werden.
     *
     * @param {KeyboardEvent} e
     * @param {Object} user - Aktueller User
     * @param {number} cols - Bildschirmbreite
     */
    async handleKeydown(e, user, cols) {
        if (!this.isOpen) return

        switch (e.key) {
            case 'Escape':
                e.preventDefault()
                this.close()
                return

            case 'ArrowUp':
                e.preventDefault()
                this._selectedIndex = Math.max(0, this._selectedIndex - 1)
                this._render(user, cols)
                return

            case 'ArrowDown':
                e.preventDefault()
                this._selectedIndex = Math.min(this._items.length - 1, this._selectedIndex + 1)
                this._render(user, cols)
                return

            case 'Enter':
                e.preventDefault()
                await this._executeAction(this._items[this._selectedIndex]?.action)
                return

            default:
                // [K]-Hotkey: ein-Zeichen-Tasten gegen item.key matchen
                if (e.key.length === 1) {
                    const upper = e.key.toUpperCase()
                    const idx = this._items.findIndex(it => it.key === upper)
                    if (idx >= 0) {
                        e.preventDefault()
                        await this._executeAction(this._items[idx].action)
                    }
                }
        }
    }

    /**
     * Mouse-Move-Handler: laesst die Selektion (Stripe) den Action-Items
     * folgen, analog zum Hauptmenue. Header (Name/Email) und Divider
     * werden uebergangen — sie sind nicht selektierbar.
     *
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn Pointer ueber dem Menue liegt
     */
    handleMouseMove(charX, charY) {
        if (!this.isOpen) return false

        const inside = charX >= this._menuX
            && charX < this._menuX + this._menuWidth
            && charY >= this._menuY
            && charY < this._menuY + this._menuHeight
        if (!inside) return false

        const itemIndex = charY - this._itemStartY
        if (itemIndex >= 0 && itemIndex < this._items.length
            && itemIndex !== this._selectedIndex) {
            this._selectedIndex = itemIndex
            this._render(this._user, this._cols)
        }
        return true
    }

    /**
     * Klick-Handler.
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn Klick verarbeitet
     */
    async handleClick(charX, charY) {
        if (!this.isOpen) return false

        // Ausserhalb des Menüs → schliessen
        if (charX < this._menuX || charX >= this._menuX + this._menuWidth ||
            charY < this._menuY || charY >= this._menuY + this._menuHeight) {
            this.close()
            return true
        }

        // Item-Bereich
        const itemIndex = charY - this._itemStartY
        if (itemIndex >= 0 && itemIndex < this._items.length) {
            this._selectedIndex = itemIndex
            await this._executeAction(this._items[itemIndex]?.action)
            return true
        }

        return true
    }

    /**
     * Fuehrt eine Menü-Aktion aus.
     */
    async _executeAction(action) {
        this.close()

        switch (action) {
            case 'editMemory':
                if (this._onEditMemory) {
                    try { await this._onEditMemory() } catch (err) {
                        console.warn('Edit Memory failed:', err.message)
                    }
                }
                break

            case 'editPersonalDict':
                if (this._onEditPersonalDict) {
                    try { await this._onEditPersonalDict() } catch (err) {
                        console.warn('Edit Personal Dictionary failed:', err.message)
                    }
                }
                break

            case 'admin':
                window.open('/app/admin/', '_blank')
                break

            case 'feedback': {
                // Kontaktformular der Website in neuem Tab, E-Mail vorbefuellt.
                // Absoluter Pfad: der Editor laeuft unter /app/, das Formular
                // liegt im Website-Root.
                const email = this._user && this._user.email ? this._user.email : ''
                const query = email ? `?email=${encodeURIComponent(email)}` : ''
                window.open(`/contact.html${query}`, '_blank')
                break
            }

            case 'logout':
                await this._handleLogout()
                break

            case 'delete':
                await this._handleDeleteAccount()
                break
        }
    }

    /**
     * Logout-Flow mit Bestätigungsdialog.
     */
    async _handleLogout() {
        const confirmed = await this._dialogManager.showConfirm(
            'Logout',
            'Do you really want to log out?'
        )
        if (!confirmed) return

        try {
            await fetch('/api/auth/logout', { method: 'POST' })
        } catch {
            // Ignorieren — Cookie loeschen auf Server nicht kritisch
        }

        // Lokale Daten bereinigen
        this._authService.clearAllLocalData()
        localStorage.removeItem('carbisdale_tabs')
        localStorage.removeItem('carbisdale_last_project')
        localStorage.removeItem('carbisdale_settings')

        window.location.reload()
    }

    /**
     * Account-Loeschung mit Doppel-Bestätigung.
     */
    async _handleDeleteAccount() {
        const confirmed = await this._dialogManager.showConfirm(
            'Delete account',
            'Your account and all cloud data will be',
            'marked for deletion (30-day grace period).'
        )
        if (!confirmed) return

        const reallyConfirmed = await this._dialogManager.showConfirm(
            'Final warning',
            'This cannot be undone within 30 days.',
            'Proceed?'
        )
        if (!reallyConfirmed) return

        try {
            await fetch('/api/auth/account', { method: 'DELETE' })
        } catch {
            // Ignorieren
        }

        this._authService.clearAllLocalData()
        localStorage.removeItem('carbisdale_tabs')
        localStorage.removeItem('carbisdale_last_project')
        localStorage.removeItem('carbisdale_settings')

        window.location.reload()
    }
}
